import React, { useContext, useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useReactToPrint } from "react-to-print";

import ReportSidebar from "./ReportSidebar";
import { SettingsContext } from "../../../../contexts/Settings";
import { UserContext } from "../../../../contexts/User";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";
import RcrReportTemplate from "./RcrReportTemplate";
import { _t, customStyle, getCookie, getSystemSettings } from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

const RcrReport = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  let { loading, dataPaginating, setDataPaginating, generalSettings } = useContext(SettingsContext);
  const { authUserInfo } = useContext(UserContext);
  const { branchForSearch } = useContext(RestaurantContext);

  const [reportData, setReportData] = useState(null);
  const componentRef = useRef();
  const componentRefThermal = useRef();
  const [auditDate, setAuditDate] = useState(new Date());
  const [branch, setBranch] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(false);
  const [option, setOption] = useState("excel");

  let common_toast = {
    position: "bottom-center",
    closeButton: false,
    autoClose: 10000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    className: "text-center toast-notification",
  };

  useEffect(()=>{
    if (id) {
      getReport();
    }
  },[id])

  const handleOption = (e) => {
    setOption(e.target.value);
  };

  const thermalPrint = useReactToPrint({
    content: () => componentRefThermal.current,
    pageStyle: `@page { margin: 15px 7px; } @media print { * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }`,
  });

  const getExcelReport = () => {
    setReportData(null);
    setGeneratedReport(false);
    if (id) {
      getReport();
    } else if (auditDate !== null && branch) {
      getReport();
    } else {
      toast.error(`${_t(t("Please select date and branch"))}`, common_toast);
    }
  };

  const getReport = async () => {
    if (
      !id &&
      (authUserInfo.details.user_type === "staff" || authUserInfo.details.user_type === "manager") &&
      !branch?.id
    ) {
      toast.error(`${_t(t("Please select branch to generate report"))}`, common_toast);
      return;
    }
    setGeneratedReport(false);
    setDataPaginating(true);

    let apiUrl = "";
    if (id) {
      apiUrl = `${BASE_URL}/settings/dashboard-report?audit_id=${id}`;
    } else {
      let selectedBranchId = branch?.id || "";
      let auditDateStr = moment(auditDate).format("YYYY-MM-DD");
      apiUrl = `${BASE_URL}/settings/dashboard-report?audit_date=${auditDateStr}&branch_id=${selectedBranchId}`;
    }

    try {
      const response = await axios.get(apiUrl, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        }
      );
      
      if (response.data) {
        setReportData(response.data);
        setGeneratedReport(true);
      }

      // setTimeout(() => {
      //   setGeneratedReport(false);
      // }, 5000);
    } catch (error) {
      toast.error("Failed to fetch report data", common_toast);
    } finally {
      setDataPaginating(false);
    }
  };

  const handleDownloadAction = () => {
    if (option === "excel") {
      componentRef.current.handleDownload();
    } else {
      thermalPrint();
    }
  };



  return (
    <>
      <Helmet>
        <title>{_t(t("Restaurant Compliance Report"))}</title>
      </Helmet>
      
      <ReactHTMLTableToExcel
        id="test-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls"
        filename={"Restaurant Compliance Report"}
        sheet="sheet 1"
        buttonText="Download Report"
        ref={componentRef}
      />

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                  {/* Sticky Header Section */}
                  <div className="bg-white position-sticky t-pl-15 t-pr-15" style={{ top: 0, zIndex: 10, borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
                    <div className="gx-2 align-items-center t-pt-15">
                      <div className="row t-mb-15 mb-md-0">
                        <ul className="t-list fk-breadcrumb col-6">
                          <li className="fk-breadcrumb__list">
                            <span className="pl-3 t-link fk-breadcrumb__link text-capitalize font-weight-bold" style={{ fontSize: '18px' }}>
                              {_t(t("Restaurant Compliance Report"))}
                            </span>
                          </li>
                        </ul>
                        <div className="col-6 d-flex justify-content-end align-items-center">
                          <ReportTypeOptions selectedOption={option} onChange={handleOption} />
                        </div>
                      </div>
                    </div>
                    
                    <div className="row mt-3">
                      {!id && (
                        <>
                          <div className="col-md-3 mb-2 mb-md-0">
                            <DatePicker
                              selected={auditDate}
                              onChange={(date) => setAuditDate(date)}
                              peekNextMonth
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              className="form-control rounded-lg shadow-sm border-0 bg-light"
                              placeholderText={_t(t("Select date"))}
                              shouldCloseOnSelect={false}
                            />
                          </div>

                          <div className="col-md-5 mb-2 mb-md-0">
                            <Select
                              options={branchForSearch || []}
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option.id}
                              value={branch}
                              styles={{
                                ...customStyle,
                                control: (base) => ({
                                  ...base,
                                  border: 'none',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                  boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)',
                                })
                              }}
                              isClearable
                              classNamePrefix="select"
                              className="table-text"
                              onChange={(branch) => setBranch(branch)}
                              maxMenuHeight="200px"
                              placeholder={_t(t("Select branch")) + ".."}
                            />
                          </div>
                        </>
                      )}
                      
                      <div className={`col-md-4 d-flex justify-content-md-end justify-content-start align-items-center ${id ? 'ml-auto' : ''}`}>
                        <button
                          className="btn btn-primary sm-text py-2 rounded-lg font-weight-bold shadow-sm mr-2"
                          style={{ width: '150px', letterSpacing: '0.5px' }}
                          onClick={getExcelReport}
                        >
                          GENERATE REPORT
                        </button>
                        {generatedReport && (
                          <button
                            className="btn btn-success sm-text py-2 rounded-lg font-weight-bold shadow-sm"
                            style={{ width: '130px', letterSpacing: '0.5px' }}
                            onClick={handleDownloadAction}
                          >
                            DOWNLOAD
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Scrollable Report Section */}
                  <div style={{height: "calc(100vh - 14.8rem)"}} className="table-bottom-info-hide" data-simplebar>
                    <div className="t-pl-15 t-pr-15 py-3">
                      <CustomSpinLoader loading={loading || dataPaginating} />

                      {generatedReport && reportData && (
                        <RcrReportTemplate 
                          ref={componentRefThermal} 
                          reportData={reportData} 
                          generalSettings={generalSettings} 
                        />
                      )}
                    </div>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default RcrReport;
